import { useCallback, useEffect, useState } from 'react';
import { addCustomerNote, getCustomerTimeline } from '@/features/customers/api';
import type { CustomerTimelineEntry } from '@/features/customers/types';
import { editNote } from '@/features/operations/api';
import type { NoteEntry } from '@/features/operations/types';

/// `appendNote` adds a note created elsewhere (the WhatsApp contact) without a reload. The errors
/// of `add` and `edit` are the panel's own words: the form shows whatever they throw.
export function useCustomerTimeline(token: string | null, customerId: string) {
  const [entries, setEntries] = useState<CustomerTimelineEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!token) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    getCustomerTimeline(token, customerId)
      .then((loaded) => {
        if (!cancelled) {
          setEntries(loaded);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('No pudimos cargar el historial. Revisá la conexión e intentá de nuevo.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token, customerId, version]);

  const reload = useCallback(() => setVersion((current) => current + 1), []);

  const appendNote = useCallback((note: NoteEntry) => {
    setEntries((current) => [
      ...current,
      { id: `note:${note.id}`, type: 'NOTE', occurredAt: note.createdAt, note },
    ]);
  }, []);

  const add = useCallback(
    async (body: string) => {
      if (!token) {
        return;
      }

      try {
        appendNote(await addCustomerNote(token, customerId, body));
      } catch {
        throw new Error('No pudimos agregar la nota. Intentá de nuevo.');
      }
    },
    [token, customerId, appendNote],
  );

  const edit = useCallback(
    async (noteId: string, body: string) => {
      if (!token) {
        return;
      }

      try {
        const updated = await editNote(token, noteId, body);
        setEntries((current) =>
          current.map((entry) =>
            entry.type === 'NOTE' && entry.note.id === updated.id
              ? { ...entry, note: updated }
              : entry,
          ),
        );
      } catch {
        throw new Error('No pudimos guardar la nota. Intentá de nuevo.');
      }
    },
    [token],
  );

  return { entries, isLoading, error, add, edit, appendNote, reload };
}
